import ora from 'ora';
import * as npmWrapper from './npmWrapper';
import { getLogger } from './logger';
import { getCliLocation } from '../utils';
import { lazyDependencies } from '../../package.json';
import { requireWithFallback } from './requireWithFallback';

const logger = getLogger('lazy-require');
const ongoingCalls = new Map();

export async function lazyRequire(dependency: string, options: { silent?: boolean } = {}) {
    const packageLocally = npmWrapper.getPackageIfInstalledLocally(dependency);
    if (packageLocally) {
        return packageLocally;
    }

    let spinner;
    if (!options.silent) {
        spinner = ora(`Installing ${dependency} before first usage...`).start();
    }

    try {
        const requiredModule = await memoizedLazyRequire(dependency);
        if (spinner) {
            spinner.succeed();
        }
        return requiredModule;
    } catch (error) {
        logger.warn('failed to install dependency lazily', { dependency, err: error });
        const depVersionToInstall = getVersionOfLazyDep(dependency);
        const depWithVersions = `${dependency}@${depVersionToInstall}`;

        const removeGlobal = process.argv.includes('npx');
        const globalFlag = removeGlobal ? '' : '-g ';
        const errorMessage = `Installation of ${dependency} failed. This typically means you are running an out-of-date version of Node.js or NPM.` +
        `Please manually install by running the following command: npm install ${globalFlag}${depWithVersions}`;

        if (spinner) {
            spinner.fail(errorMessage);
        }

        throw error;
    }
}

async function memoizedLazyRequire(identifier: string) {
    if (ongoingCalls.has(identifier)) {
        return ongoingCalls.get(identifier);
    }

    ongoingCalls.set(identifier, lazyRequireImpl(identifier));
    ongoingCalls.get(identifier).catch(() => {
        ongoingCalls.delete(identifier);
    });

    return ongoingCalls.get(identifier);
}

async function lazyRequireImpl(dependency: string) {
    const packageLocally = npmWrapper.getPackageIfInstalledLocally(dependency);

    if (packageLocally) {
        return packageLocally;
    }

    const depVersionToInstall = getVersionOfLazyDep(dependency);

    const depWithVersions = `${dependency}@${depVersionToInstall}`;

    await npmWrapper.installPackageLocally(getCliLocation(), depWithVersions);

    return requireWithFallback(dependency);
}

export async function installAllLazyDependencies() {
    const allLazyDependencies = Object.keys(lazyDependencies);

    for (const dep of allLazyDependencies) {
        await lazyRequireImpl(dep);
    }
}

function getVersionOfLazyDep(dependencyToFind: string) {
    const depEntry = Object.entries(lazyDependencies)
        .find(([dep]) => dep === dependencyToFind);

    if (!depEntry) {
        throw new Error('The given package name is not lazyDependencies');
    }

    return depEntry[1];
}
